import React from "react";
import { Divider } from "antd";
import Helmet from "react-helmet";
import { Container } from "./styled";

export default () => (
  <Container>
    <Helmet title="Help" />
    <h1>Help</h1>
    <p>
      Please note that the FBQ system is currently under development -the help info available on this page may not always be up to date.{" "}
    </p>
    <Divider />
    <h2>Video Tutorials</h2>
    <p>We have posted a few videos on how to use the FBQ system on Youtube - the links below will take you to Youtube to view them.</p>
    <p><a href="https://youtu.be/d_FtAE9RFKw" target="_blank">Connecting to Etsy and Creating Mappings</a></p>
    <p><a href="https://youtu.be/0DNq1lskMxE" target="_blank">Creating Mappings in Bulk</a></p>
    <p><a href="https://youtu.be/YQTfcZWSk98" target="_blank">Bulk Orders from Mappings</a></p>
    <Divider />
    <h2>Key Concept: Mappings</h2>
    <p>
      <b>Mapping:</b> a set of digital images matched up to a specific product
      offered by our lab that defines what is needed when an order for a
      product/variation from your store is received.
    </p>
    <p>
      Mappings exist on their own and do not need to be linked to a specific
      product you offer. Since they are separate they can then be linked to a
      product/variation you offer on multiple different platforms though
      currently we only support Etsy. In the future we plan to integrate with
      other platforms such as Amazon. Once we can support more than one platform
      the fact that Mappings exist on their own will make more sense.
    </p>
    <p>
      <b>Example of a Mapping:</b> 4 images of a sunset, matched up to a Luster
      finish 11x14 print size. This could then be linked up to a product on Etsy
      that offers a set of 4 11x14 luster finish prints of sunsets.
    </p>
    <p>We support 3 types of Mappings:</p>
    <ol>
      <li>Static</li>
      <li>Custom</li>
      <li>Multiple Choice</li>
    </ol>
    <p>
      <b>Static Mappings:</b> A Static Mapping is one where the same exact
      image(s) will be printed as the same type/size of print for every order.
      If you are selling a set of prints of a given type of artwork that cannot
      be customized and the buyer always gets the same thing then that is a
      Static Mapping. You can still edit what is in the Mapping if you change
      one of the files to make it better for example, but from that point
      forward every new order would receive that as well. A product that is
      linked to a Static Mapping can be fulfilled by just clicking a button
      telling us to Fulfill it. Eventually we will also allow New Orders for
      products with a Static Mapping to be fulfilled automatically.
    </p>
    <p>
      <b>Custom Mappings:</b> A custom Mapping is one where the files to be
      printed do not exist in advance of the order, such as an image that needs
      to be customized with info provided by the buyer - like a print with their
      name on it. When the order for a custom product comes in, you would
      fulfill it in our system by providing the image(s) needed and choosing the
      size and type of print needed. Custom Mappings are created on the fly as
      you Customize & Fulfill an order. Custom Mappings will not show up on the
      Mappings page as they only exist while fulfilling a specific order.
    </p>
    <p>
      <b>Multiple Choice Mappings:</b> A Multiple Choice Mapping is like a
      Static Mapping that can be customized. If you offer a product where the
      buyer can choose 4 images from a group of 12 images and have just those 4
      images printed as a certain size and type of print then that is a Multiple
      Choice Mapping. To do this you would create a Mapping with all 12 images
      and set them to be printed at a certain size and type of print, and choose
      the 'Multiple choice item' checkbox option. When an order for a product
      that is linked to this Mapping is received, you will be able to delete the
      images not needed and send the order for fulfillment.
    </p>
    <p />
    <h2>Creating Mappings and Fulfilling Orders</h2>
    <p>
      In order for us to fulfill an order from your store, you will need to
      create Mappings that can be linked to a product/variation that has been
      ordered. If all of your orders are for customized products, then you may
      not need to create Mappings in advance as each order would be fulfilled
      using a Custom Mapping. All you would need to do to fulfill a new order is
      to click the Customize & Fulfill button and add the needed files and
      choose the type and size of print needed.
    </p>
    <p>
      More than likely not all your products will be custom, so you will need to
      create Mappings and link them to your product/variations. There are 3 ways
      to do this:
    </p>
    <ol>
      <li>Create Mappings in Bulk</li>
      <li>Create Mappings from New Orders</li>
      <li>Create Mappings from the Listings in your Store</li>
      <li>Create Mappings from Mappings page, then Link to a Product/Variation
        from your Store Listings
      </li>
    </ol>
    <p><b>Create Mappings in Bulk</b> If you have a large number of mappings - especially if you have prefixed your files with a SKU - Bulk Mappings are the way to go. From the Mappings page click on the Bulk Mapping page and you will see some mapping options at the top and a Select Files button to load your files in to the interface. On the right side are the individual mapping options for the SKU, Catalog, Product, Qty and Name. Once you load the files via the Select Files button and set the options above you can begin creating mappings by selecting an image from the list.</p>
    <p>If your files have a SKU prefix and you chose the Parse SKU from Filename option, the system will attempt to separate the SKU prefix into the SKU text box, and will put the rest of the filename into the Name text box (minus any numbers at the end). The system will also automatically select any other files with the same SKU prefix and add them to the mapping. When you choose Parse SKU from Filename you will also be able to select the separator - such as a space or dash, this can get complicated for the system to sort out.</p>
    <p>If your files do not have a SKU, you will need to manually select the files that are needed for each mapping, the system will attempt to populate the Name field for you using the filename of the first checked file minus any numbers at the end.</p>
    <p>The Catalog and Product options will stay once you set them, we recommend you create your Mappings in batches of the same Catalog and Product to limit confusion</p>
    <p>Once you click the Create Mapping button the system will begin uploading the images and creating the Mapping. Pending Mappings will go into a queue and will begin uploading when it is their turn. You must keep the page open while the mappings are uploading for the process to complete.</p>
    <p>While uploading the system will check for duplicate SKU's and Mapping names - they both must be unique. If a duplicate is found there will be a red message next to the Mapping name telling you it is a duplicate.</p>
    <p>There is a Mapping option for Quantity, this refers to the number of prints needed for each file in the Mapping. This will almost always be 1.</p>
    <p>
      <b>Create Mappings from New Orders:</b> This is one of the easiest ways to
      get started. You have a new order, there are no mappings, but from the New
      Orders tab you can click 'Customize & Fulfill' and a form will pop up to
      the side that will allow you to create a Mapping. When you create a
      Mapping from a New Order the form will have a checkbox option for 'Save
      mappings for future orders' which when checked will save the Mapping you
      just created and link it to the product/variation of the order you are
      fulfilling. So when you check the box to save the Mapping, any new orders
      for that same product/variation will automatically already be linked to
      the Mapping and you will be able to fulfill new orders by just clicking
      Fulfill.
    </p>
    <p>
      <b>Create Mappings from the Listings in your Store:</b> If you want to
      create a lot of Mappings to your products all at one time you can do this
      from the Store Listings page. On the Store Listings page we display all of
      your Store Products in a gallery. Clicking on any item will bring up the
      listing details where any Variations for that product will be shown, along
      with any Mappings that have been linked. If you have a product with a
      Variation of Size, and sizes of 5x7, 8x10, 11x14 and 16x20 you will end up
      needing 4 Mappings - one for each size. If you also have a Variation
      called Tone with options for Color and Black and White, then you would
      need a total of 8 Mappings, as there will be a total of 8 different
      combinations of size and tone.
    </p>
    <p>
      <b>
        Create Mappings from Mappings page, then Link to a Product/Variation
        from your Store Listings:
      </b>{" "}
      It is also possible to create a new Mapping on the Mappings page, and then
      link that Mapping to a product/variation on either the Product Listings
      page or the New Orders tab. While this is possible it is not recommended
      as the other ways already mentioned give you a product/variation to work
      off of first - and that product/variation name is pre-populated as the
      name of the Mapping.
    </p>
    <h2>Orders from Mappings</h2>
    <p>You can order directly off your Mappings in by clicking on the Create an Order button on the Mappings page or the Home page. You will then see a list of all your Mappings, along with the Catalog, Size, Number of Files, Date Modified, and History (number of times that Mapping has been ordered in bulk). You can click on any of the columns to sort by that column. You can add a Mapping to the order by clicking the +1, +5 or +50 buttons to add that quantity of mappings to the order, or you can manually enter a number of Mappings to order.</p>
    <p><b>Order from CSV file:</b> On the Create an Order page there is a button to import a CSV file. As soon as you import the CSV file you will be able to match the columns in the CSV up to the Mapping SKU/Name and specify which column is the quantity. As soon as you do that and click Next the Mapping list on the order screen will fill with the quantities requested in the CSV. </p>
    <p>Once you are done ordering, select the shipping method - Regular shipping which will ship to the address in your Profile, or Dropship to ship somewhere else.</p>
    <p>Once you checkout the process should finish quickly as the files are already uploaded. You will then receive an email with a summary of the order, and a link to pay (if you have a credit card on file you can ignore the payment link).</p>
    <h2>Hiding Etsy Orders from the New Orders Tab</h2>
    <p>If you have products on Etsy that you will not fulfill thru our system you can tell our system to ignore them so they will not show up on the New Orders tab. There are 2 ways to do this:</p>
    <ol>
      <li>Ignore all unmapped orders - this is a setting on the MyStores page, click the Edit icon under your Etsy store name and select the checkbox to "Hide orders without a mapping from the Orders page". If checked any orders for an Etsy product variation that does not have a linked mapping will not show on the New Orders tab.</li>
      <li>Ignore specific product variations - this is a setting found on the Store Listings page, select a product, click on the Assign Mapping box and there will be an option called "Ignore this product". Any orders for that product variation will not show on the New Orders tab.</li>
    </ol>
  </Container>
);
